<template>
    <common-content-spacer height="huge">
        <div v-if="isRescheduleByAvailabilityChange" class="availability-reschedule-modal__header referral-details__warning">
            <common-icon name="white-warning" size="huge"></common-icon>
            <span>
                {{ $t('pages.referrals.show.notifications.out_of_availability') }}
            </span>
        </div>
        <section v-if="isReferralError(referral) && !isReferred" class="referral-details">
            <div class="referral-details-content">
                <div class="referral-details-content__width-half">
                    <common-summary :isCompact="true">
                        <common-summary-item
                            :label="$t('pages.referrals.show.details.specialty')"
                            :isInline="true"
                            :isCompact="true"
                            width="full"
                        >
                            {{ referral.speciality?.name }}
                        </common-summary-item>

                        <common-summary-item
                            :label="$t('pages.referrals.show.details.diagnosis')"
                            :isInline="true"
                            :isCompact="true"
                            width="full"
                        >
                            {{ referral.diagnosis }}
                        </common-summary-item>
                        <common-summary-item
                            v-if="shouldShowFacilityTests"
                            :label="$t('pages.referrals.show.details.tests')"
                            :isInline="true"
                            :isCompact="true"
                            width="full"
                        >
                            {{ formattedTests() }}
                        </common-summary-item>

                        <common-summary-item
                            :label="$t('pages.referrals.show.details.insurance')"
                            :isInline="true"
                            :isCompact="true"
                            width="full"
                        >
                            {{ referral.insurancePlan.name }}
                        </common-summary-item>
                        <hr class="referral-details-content__width-half referral-details-content-hr" />
                        s
                        <common-summary-item
                            :label="$t('pages.referrals.show.details.insurance_authorization_status')"
                            :isCompact="true"
                            width="full"
                        >
                            {{
                                referral.isInsuranceAuthorizationRequired
                                    ? $t('pages.referrals.show.details.insurance_authorization_statuses.required')
                                    : $t('pages.referrals.show.details.insurance_authorization_statuses.not_required')
                            }}
                        </common-summary-item>
                    </common-summary>
                </div>
                <div class="referral-details-content__width-half">
                    <common-summary :isCompact="true">
                        <common-summary-item
                            :label="$t('pages.referrals.show.details.location')"
                            :isInline="true"
                            :isCompact="true"
                            width="full"
                        >
                            {{ referral.practiceLocation.address }}, {{ referral.practiceLocation.state }}
                        </common-summary-item>

                        <common-summary-item
                            :label="$t('pages.referrals.show.details.patient_name')"
                            :isInline="true"
                            :isCompact="true"
                            width="full"
                        >
                            <common-patient-name :patient="referral.patient"></common-patient-name>
                        </common-summary-item>

                        <common-summary-item :label="''" :isInline="true" :isCompact="true" width="full">
                            <common-icon-info
                                v-if="referral.patient.email"
                                :href="`mailto:${referral.patient.email}`"
                                icon="email"
                                size="medium"
                            >
                                {{ referral.patient.email }}
                            </common-icon-info>
                        </common-summary-item>

                        <common-summary-item :label="''" :isInline="true" :isCompact="true" width="full">
                            <common-icon-info v-if="referral.patient.mobilePhone" icon="phone" size="medium">
                                {{ referral.patient.mobilePhone }}
                            </common-icon-info>
                        </common-summary-item>

                        <common-summary-item
                            :label="$t('pages.referrals.show.details.date_of_birth')"
                            :isInline="true"
                            :isCompact="true"
                            width="full"
                        >
                            {{ patientBirthDate }}
                        </common-summary-item>
                    </common-summary>
                </div>
            </div>
        </section>
        <section v-else class="referral-details">
            <common-summary :isCompact="true">
                <common-summary-item
                    :label="$t('pages.referrals.show.details.diagnosis')"
                    :isInline="true"
                    :isCompact="true"
                    width="full"
                >
                    {{ referral.diagnosis }}
                </common-summary-item>
                <common-summary-item
                    v-if="shouldShowFacilityTests"
                    :label="$t('pages.referrals.show.details.tests')"
                    :isInline="true"
                    :isCompact="true"
                    width="full"
                >
                    {{ formattedTests() }}
                </common-summary-item>

                <common-summary-item
                    :label="$t('pages.referrals.show.details.insurance')"
                    :isInline="true"
                    :isCompact="true"
                    width="full"
                >
                    {{ referral.insurancePlan.name }}
                </common-summary-item>

                <common-summary-item
                    v-if="referral.insuranceNumber"
                    :label="$t('pages.referrals.show.details.insurance_number')"
                    width="full"
                >
                    {{ referral.insuranceNumber }}
                </common-summary-item>

                <common-summary-item
                    v-if="referral.insuranceGroupNumber"
                    :label="$t('pages.referrals.show.details.insurance_group_number')"
                    width="full"
                >
                    {{ referral.insuranceGroupNumber }}
                </common-summary-item>

                <common-summary-item :label="$t('pages.referrals.show.details.insurance_authorization_status')" width="full">
                    {{
                        referral.isInsuranceAuthorizationRequired
                            ? $t('pages.referrals.show.details.insurance_authorization_statuses.required')
                            : $t('pages.referrals.show.details.insurance_authorization_statuses.not_required')
                    }}
                </common-summary-item>

                <div :class="referral.shouldShowPatientInfo ? '' : 'text-blured'">
                    <common-summary-item
                        :label="$t('pages.referrals.show.details.patient_name')"
                        :isInline="true"
                        :isCompact="true"
                        width="full"
                    >
                        <common-patient-name
                            :patient="referral.patient"
                            :clickable="Boolean(referral.shouldShowPatientInfo)"
                        ></common-patient-name>
                    </common-summary-item>

                    <common-summary-item :isCompact="true" width="full">
                        <common-content-spacer height="tiny">
                            <template v-if="referral.patient.mobilePhone">
                                <div>
                                    <common-icon-info icon="phone" size="medium">
                                        {{ referral.patient.mobilePhone }}
                                    </common-icon-info>
                                </div>
                            </template>
                        </common-content-spacer>
                    </common-summary-item>

                    <common-summary-item
                        :label="$t('pages.referrals.show.details.date_of_birth')"
                        :isInline="true"
                        :isCompact="true"
                        width="full"
                    >
                        {{ patientBirthDate }}
                    </common-summary-item>
                </div>

                <div v-if="!referral.shouldShowPatientInfo" class="referral-details__notification">
                    {{ $t('pages.referrals.show.notifications.patient_information_hidden') }}
                </div>
            </common-summary>
            <div class="referral-details__date">
                <common-content-spacer height="small">
                    <referrals-datetime :referral="referral"></referrals-datetime>
                    <subscribe-to-premium-tooltip-wrapper :show="isToUserSubscriptionNetwork">
                        <common-button
                            v-if="showRescheduleButton"
                            :label="$t('pages.referrals.show.buttons.reschedule')"
                            :disabled="isToUserSubscriptionNetwork"
                            icon="reschedule"
                            type="tertiary"
                            size="small"
                            @click="openDisclaimerModal()"
                        ></common-button>
                    </subscribe-to-premium-tooltip-wrapper>
                    <pending-reschedule-modal
                        v-if="isRescheduleModalOpen"
                        :referral="referral"
                        @deny="rescheduleDenyAction"
                        @reschedule="emitEvent({ name: 'reschedule', payload: $event })"
                        @close="closeRescheduleModal()"
                    ></pending-reschedule-modal>
                </common-content-spacer>
            </div>
        </section>

        <common-page-header
            v-if="isRescheduleByAvailabilityChange"
            class="referral-details__heading"
            :heading="rescheduleAvailabilityChangeHeader"
            :isCenter="true"
            size="large"
        ></common-page-header>

        <common-buttons>
            <common-button
                v-if="isRescheduleByAvailabilityChange"
                :label="$t('pages.referrals.show.buttons._back')"
                size="large"
                type="secondary"
                @click="$router.go(-1)"
            ></common-button>
            <subscribe-to-premium-tooltip-wrapper :show="isToUserSubscriptionNetwork">
                <common-button
                    v-if="showDeclineButton"
                    :label="$t('pages.referrals.show.buttons.decline')"
                    :disabled="isToUserSubscriptionNetwork"
                    size="large"
                    type="secondary"
                    @click="openDeclineModal()"
                ></common-button>
            </subscribe-to-premium-tooltip-wrapper>
            <common-button
                v-if="showCancelButton"
                :label="$t('pages.referrals.show.buttons.cancel')"
                size="large"
                type="primary"
                @click="openCancellationModal()"
            ></common-button>

            <common-button
                v-if="isRescheduleByAvailabilityChange"
                :label="$t('pages.referrals.show.buttons.reschedule')"
                size="large"
                type="primary"
                @click="openDisclaimerModal()"
            ></common-button>
            <subscribe-to-premium-tooltip-wrapper :show="isToUserSubscriptionNetwork">
                <common-button
                    v-if="showConfirmButton"
                    :label="$t('pages.referrals.show.buttons.accept')"
                    :disabled="isToUserSubscriptionNetwork"
                    size="large"
                    type="primary"
                    @click="confirmReferral()"
                ></common-button>
            </subscribe-to-premium-tooltip-wrapper>
            <pending-decline-modal
                v-if="isDeclineModalOpen"
                :referral="referral"
                @decline="emitEvent({ name: 'decline', payload: $event })"
                @reschedule="openDisclaimerModal()"
                @close="closeDeclineModal()"
            ></pending-decline-modal>
            <referral-cancellation-modal
                v-if="isCancellationModalOpen"
                :referral="referral"
                :type="isReferred ? 'referred' : 'referring'"
                :withSecondOption="isReferred || isReferralError(referral)"
                @close="closeCancellationModal"
                @confirm="emitEvent({ name: 'cancel' })"
                @deny="cancellationModalDenyAction"
            ></referral-cancellation-modal>
            <disclaimer-modal
                :isOpen="isDisclaimerModalOpen"
                @close="isDisclaimerModalOpen = false"
                @continue="acceptDisclaimer()"
            ></disclaimer-modal>
        </common-buttons>
    </common-content-spacer>
</template>

<script>
    import dayjs from 'dayjs'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralsDatetime from 'components/page-elements/ReferralsDatetime'
    import PendingDeclineModal from 'components/page-elements/referrals-show-pending/PendingDeclineModal'
    import PendingRescheduleModal from 'components/page-elements/referrals-show-pending/PendingRescheduleModal'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ReferralCancellationModal from 'components/page-elements/ReferralCancellationModal'
    import DisclaimerModal from 'components/page-elements/referrals/DisclaimerModal'
    import ReferralConfirmMixin from 'mixins/ReferralConfirmMixin'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import SubscribeToPremiumTooltipWrapper from '../SubscribeToPremiumTooltipWrapper.vue'
    export default {
        mixins: [ReferralStatusMixin, ReferralConfirmMixin],

        components: {
            ReferralsDatetime,
            PendingDeclineModal,
            PendingRescheduleModal,
            ReferralCancellationModal,
            DisclaimerModal,
            SubscribeToPremiumTooltipWrapper,
        },

        props: {
            referral: { type: Object, required: true },
            requestedAction: { type: Object, required: false, default: null },
            isToUserSubscriptionNetwork: { type: Boolean, required: true, default: false },
        },

        data() {
            return {
                isDeclineModalOpen: false,
                isRescheduleModalOpen: false,
                isCancellationModalOpen: false,
                isDisclaimerModalOpen: false,
                PracticeTypeEnum: PracticeTypeEnum,
            }
        },

        computed: {
            shouldShowFacilityTests() {
                return (
                    this.referral?.facilityServiceTests &&
                    this.referral.facilityServiceTests.length > 0 &&
                    this.referral?.practice?.practiceType === PracticeTypeEnum.ANCILLARY
                )
            },
            isReferred() {
                return this.$route.params.type === 'received'
            },

            patientBirthDate() {
                return dayjs(this.referral.patient.dateOfBirth).format('MM/DD/YYYY')
            },

            showDeclineButton() {
                return (
                    this.isReferred &&
                    !this.isReferralPendingReschedule(this.referral) &&
                    !this.isReferralConfirmed(this.referral)
                )
            },

            showCancelButton() {
                return (
                    !this.isReferralPendingReschedule(this.referral) &&
                    this.isReferralConfirmed(this.referral) &&
                    !this.isReferralFinished(this.referral) &&
                    !this.isReferralCancelledOrNoShow(this.referral)
                )
            },

            showConfirmButton() {
                return (
                    this.showDeclineButton &&
                    !this.isAppointmentInPast(this.referral) &&
                    !this.isReferralToBeRescheduled(this.referral)
                )
            },

            showRescheduleButton() {
                return this.isReferred && !this.isReferralToBeRescheduled(this.referral)
            },

            isRescheduleByAvailabilityChange() {
                return this.isReferred && this.isReferralToBeRescheduled(this.referral)
            },

            rescheduleAvailabilityChangeHeader() {
                return this.$t(`pages.referrals.show.buttons.heading_${this.showCancelButton ? 'cancel' : 'decline'}_reschedule`)
            },
        },

        methods: {
            formattedTests() {
                if (!this.referral?.facilityServiceTests) return ''
                return this.referral.facilityServiceTests.map((test) => `${test.name} - ${test.group?.name}`).join(', ')
            },
            openCancellationModal() {
                this.isCancellationModalOpen = true
            },

            closeCancellationModal() {
                this.isCancellationModalOpen = false
            },

            openDeclineModal() {
                this.isDeclineModalOpen = true
            },

            closeDeclineModal() {
                this.isDeclineModalOpen = false
            },

            cancellationModalDenyAction() {
                this.closeCancellationModal()
                if (this.isReferred) {
                    this.openDisclaimerModal()
                } else {
                    this.emitEvent({ name: 'match' })
                }
            },

            rescheduleDenyAction() {
                this.closeRescheduleModal()
                if (this.isReferralConfirmed(this.referral)) {
                    this.openCancellationModal()
                } else {
                    this.openDeclineModal()
                }
            },

            openRescheduleModal() {
                if (this.referral.status === 'RESCHEDULE_REQUESTED') {
                    this.emitEvent({ name: 'reschedule' })
                    return
                }
                this.isRescheduleModalOpen = true
            },

            acceptDisclaimer() {
                this.isDisclaimerModalOpen = false
                ReferralsAPI.acceptDisclaimer(this.referral.id).then((res) => {
                    this.openRescheduleModal()
                    this.emitEvent({ name: 'referral-changed', payload: res })
                })
            },

            openDisclaimerModal() {
                if (this.referral.shouldShowPatientInfo) this.openRescheduleModal()
                else this.isDisclaimerModalOpen = true
            },

            closeRescheduleModal() {
                this.isRescheduleModalOpen = false
            },

            confirmReferral() {
                this.emitEvent({ name: 'confirm' })
            },

            matchReferral() {
                this.emitEvent({ name: 'match' })
            },

            handleRequestedAction() {
                if (this.requestedAction && this.requestedAction.action) {
                    switch (this.requestedAction.action) {
                        case 'confirm': {
                            this.confirmReferral()
                            break
                        }
                        case 'reschedule': {
                            this.openDisclaimerModal()
                            break
                        }
                        case 'decline': {
                            this.openDeclineModal()
                            break
                        }
                        case 'cancel': {
                            this.openCancellationModal()
                            break
                        }
                        case 'match': {
                            this.matchReferral()
                            break
                        }
                    }
                }
            },
        },

        watch: {
            requestedAction: function () {
                this.handleRequestedAction()
            },
        },

        mounted() {
            this.handleRequestedAction()
        },
    }
</script>
